import type { Destination as ApiDestination, InputView, OutputView, Profile, StreamKey, UserView, VerticalKey } from '../api'
import { wireKbps } from './wire'
import { t } from '../i18n'

/*
 * VERT-1 · V4 (docs/vertical.md §3, §5.2, §7; mockup design/vertical/): the vertical (9:16) input of a broadcast
 * as the Studio and Status show it — which input is the main one, the pair's numbers, which outputs send the
 * vertical input and what that means now. Texts come from the dictionary (design/vertical/texts.md).
 */

/** |drift| over this is a warning: on the map node, in the inputs table and in a note (VT-16, §5.2). */
export const DRIFT_WARN_MS = 1000

/** Twitch's simulcasting rules (Q5 tip). */
export const TWITCH_RULES_URL = 'https://help.twitch.tv/s/article/simulcasting-guidelines'

/** YouTube Help on Dual stream (docs/vertical.md §4.1). */
export const YOUTUBE_DUAL_HELP_URL = 'https://support.google.com/youtube/answer/2474026'

/** The main input of the view: never the vertical half of a pair (decision 3: the sender decides). */
export function mainInput(view: UserView): InputView | undefined {
  const mains = view.inputs.filter((i) => i.role !== 'vertical')
  return mains.find((i) => i.live) ?? mains[0] ?? view.inputs[0]
}

/** The vertical input paired with this main stream, if the view carries it. */
export function verticalInput(view: UserView, main?: string): InputView | undefined {
  return view.inputs.find((i) => i.role === 'vertical' && (!main || !i.pair_of || i.pair_of === main))
}

/** The vertical key of a main key: turned on (not revoked). */
export const verticalOn = (k?: Pick<StreamKey, 'vertical'>): k is { vertical: VerticalKey } => !!k?.vertical && !k.vertical.revoked_at

/** The main key a screen talks about: the live stream's, else the first active one. */
export function mainKey(keys: StreamKey[] | undefined, streamName?: string): StreamKey | undefined {
  const mains = keys?.filter((k) => k.role !== 'vertical')
  return mains?.find((k) => k.stream_name === streamName && !k.revoked_at) ?? mains?.find((k) => !k.revoked_at) ?? mains?.[0]
}

/** Portrait platforms (catalog orientation; TikTok and Instagram while profiles load). */
export function portrait(profile: string, profiles?: Profile[]): boolean {
  const p = profiles?.find((x) => x.id === profile)
  return p?.orientation ? p.orientation === 'portrait' : profile === 'tiktok' || profile === 'instagram'
}

/** Does a destination ask for the vertical input (input vertical, or auto on a portrait platform)? (§3.1) */
export function wantsVertical(input: string | undefined, profile: string, profiles?: Profile[]): boolean {
  return input === 'vertical' || (input === 'auto' && portrait(profile, profiles))
}

/** The pair as the Studio and Status show it: the vertical input next to the main one. */
export interface PairView {
  /** the vertical input is turned on for the main key (GET /api/stream-keys vertical, not revoked) */
  on: boolean
  /** it was turned on once (a key exists, maybe revoked) */
  ever: boolean
  streamName?: string
  /** on air (the node receives it) */
  live: boolean
  /** the main input is on air too */
  mainLive: boolean
  protocol?: string
  /** the encoder, as the main source says it (OBS Studio / Your encoder) */
  app: string
  bitrateMbps?: number
  resolution?: string
  fps?: number
  keyframeSec?: number
  /** the change of the pair's offset since the start, ms (§5.2); absent while unknown */
  driftMs?: number
  /** refused by a rule: another ingest server, or not on the plan */
  refused?: 'ingest_mismatch' | 'plan_no_vertical'
  preview?: { stream: string; available: boolean }
  /** how many enabled outputs send it now or will (input vertical / auto on a portrait platform) */
  outputs: number
}

export function pairView(view: UserView, main: InputView | undefined, keys: StreamKey[] | undefined, dests: ApiDestination[] | undefined, profiles: Profile[] | undefined, nodeDown: boolean): PairView | undefined {
  const key = mainKey(keys, main?.stream_name)
  const vin = verticalInput(view, main?.stream_name)
  const on = verticalOn(key)
  if (!on && !vin) return undefined
  const live = !!vin?.live && !nodeDown
  const r = vin?.state_reason
  return {
    on,
    ever: !!key?.vertical || !!vin,
    streamName: key?.vertical?.stream_name ?? vin?.stream_name,
    live,
    mainLive: !!main?.live && !nodeDown,
    protocol: vin?.protocol,
    app: vin?.encoder && /obs/i.test(vin.encoder) ? 'OBS Studio' : 'Your encoder',
    bitrateMbps: live ? wireKbps(vin!) / 1000 : undefined,
    resolution: live ? vin!.resolution : undefined,
    fps: live && vin!.fps ? vin!.fps : undefined,
    keyframeSec: live && vin!.gop_seconds ? vin!.gop_seconds : undefined,
    driftMs: live && main?.live && vin!.pair_drift_ms !== undefined ? vin!.pair_drift_ms : undefined,
    refused: r === 'ingest_mismatch' || r === 'plan_no_vertical' ? r : undefined,
    preview: live && vin!.preview_available !== undefined ? { stream: vin!.stream_name, available: vin!.preview_available } : undefined,
    outputs: (dests ?? []).filter((d) => d.enabled && d.video_track == null && wantsVertical(d.input, d.profile, profiles)).length,
  }
}

/** The drift as people read it: "+180 ms", "+1.4 s". */
export function driftText(ms: number | undefined): string {
  if (ms === undefined) return '—'
  const sign = ms > 0 ? '+' : ms < 0 ? '−' : ''
  const a = Math.abs(ms)
  return a >= 1000 ? `${sign}${(a / 1000).toFixed(1)} s` : `${sign}${Math.round(a)} ms`
}

export const driftWarn = (ms: number | undefined) => ms !== undefined && Math.abs(ms) > DRIFT_WARN_MS

/**
 * What an output row says about the vertical input (mockup A, B2, F; VT-11, VT-17); the states come from the center's
 * input_effective (VERT-LIVE), the page computes none of them:
 * tag — the neutral 9:16 tag (the output sends, or will send, the vertical input);
 * label — the source before the broadcast ("Vertical", "Vertical (auto)");
 * state — fallback: on the main stream after the vertical input was lost (warn); off: the vertical input
 * is turned off and the output asks for it (idle, "Won't go live"); next: YouTube takes the vertical stream
 * from the next broadcast (idle).
 */
export interface DestVertical {
  tag: boolean
  label?: 'auto' | 'vertical' | 'main'
  state?: 'fallback' | 'off' | 'next'
  /** fallback: when the vertical input went (ms, the center's input_effective.vertical_lost_at); undefined: unknown */
  lostAt?: number
}

export function destVertical(o: OutputView, row: ApiDestination | undefined, opts: {
  profiles?: Profile[]; pair?: PairView; nodeDown: boolean;
  /** the main key's vertical input is turned on (GET /api/stream-keys); undefined while the keys load */
  keyOn?: boolean;
}): DestVertical | undefined {
  const input = row?.input ?? o.input
  if (!input || input === 'main') return undefined
  // an explicit E1 track wins over input (§3.1): the row shows its track, not a source
  if (row?.video_track != null) return undefined
  const profile = row?.profile ?? o.profile
  if (!wantsVertical(input, profile, opts.profiles)) return undefined
  const eff = row?.input_effective ?? o.input_effective
  const on = opts.keyOn ?? opts.pair?.on ?? eff?.state !== 'vertical_off'
  const live = o.state === 'live' && !opts.nodeDown
  const label = input === 'auto' ? 'auto' as const : 'vertical' as const
  if (!on) {
    // auto without a vertical input is the main stream: "Main", the tag says the platform is 9:16 (mockup B1)
    if (input === 'auto') return live ? undefined : { tag: true, label: 'main' }
    return { tag: true, label, state: 'off' }
  }
  const now = o.input_effective ?? eff
  if (now?.state === 'dual_next_broadcast') return { tag: true, label, state: 'next' }
  if (live && now?.state === 'fallback_main' && opts.pair?.mainLive) {
    const at = now.vertical_lost_at ? Date.parse(now.vertical_lost_at) : NaN
    return { tag: true, label, state: 'fallback', lostAt: Number.isNaN(at) ? undefined : at }
  }
  return { tag: true, label: live ? undefined : label }
}

/** Seconds since the vertical input was lost, for the row ("vertical lost 4 s ago"); undefined while unknown. */
export const lostFor = (lostAt: number | undefined, now: number) => (lostAt === undefined ? undefined : Math.max(0, Math.floor((now - lostAt) / 1000)))

/** The words of an output's source in tables (Status "Input" column, mockup E). */
export function sourceWords(d: { vertical?: DestVertical; input?: string; inputEffective?: { input: string } }): string {
  if (!d.vertical) return t('vert.row.main')
  if (d.vertical.state === 'fallback') return t('vert.row.main')
  return d.input === 'auto' ? t('vert.out.auto') : t('vert.out.vertical')
}

/** YouTube Dual stream: the pair of destinations (horizontal, vertical) of one broadcast. */
export interface DualPair<T> {
  horizontal: T
  vertical: T
}

/** Pairs of Dual stream outputs by the vertical one's dualOf; outputs without a partner stay single. */
export function dualPairs<T extends { id: number; dualOf?: number }>(ds: T[]): { pairs: DualPair<T>[]; inPair: Set<number> } {
  const pairs: DualPair<T>[] = []
  const inPair = new Set<number>()
  for (const v of ds) {
    if (v.dualOf === undefined) continue
    const h = ds.find((x) => x.id === v.dualOf)
    if (!h || inPair.has(h.id)) continue
    pairs.push({ horizontal: h, vertical: v })
    inPair.add(h.id)
    inPair.add(v.id)
  }
  return { pairs, inPair }
}
